/**
 * Transcripts Page
 * =================
 * List view of all meeting transcripts
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardHeader, CardBody, Button, Badge } from '../../components/ui';
import { DashboardLayout } from '../../components/layout';
import transcriptAPI from '../../api/transcripts';
import { useToast } from '../../hooks';

const TranscriptsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();

  const [transcripts, setTranscripts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const leadIdFilter = searchParams.get('lead_id');

  const fetchTranscripts = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page: currentPage,
        limit: pagination.limit
      };
      if (leadIdFilter) {
        params.lead_id = leadIdFilter;
      }

      const response = await transcriptAPI.listTranscripts(params);
      if (response.data) {
        setTranscripts(response.data.transcripts || []);
        setPagination(response.data.pagination || pagination);
      }
    } catch (err) {
      console.error('Error fetching transcripts:', err);
      showToast('Failed to load transcripts', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentPage, leadIdFilter, pagination.limit, showToast]);

  useEffect(() => {
    fetchTranscripts();
  }, [fetchTranscripts]);

  const handlePageChange = (newPage) => {
    const params = new URLSearchParams(searchParams);
    params.set('page', newPage.toString());
    setSearchParams(params);
  };

  const handleViewTranscript = (transcript) => {
    if (transcript.meeting_id && transcript.lead_id) {
      navigate(`/leads/${transcript.lead_id}/meetings/${transcript.meeting_id}/transcript`);
    }
  };

  const getTranscriptStatusBadge = (transcript) => {
    if (transcript.has_summary) {
      return <Badge variant="success">Complete</Badge>;
    }
    return <Badge variant="warning">Transcript Only</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'N/A';
    if (minutes < 60) return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours}h ${mins}m`;
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-[28px] font-sans font-bold text-gray-900 dark:text-white mb-2">
              Meeting Transcripts
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              View and manage transcripts from recorded meetings
            </p>
          </div>
          {leadIdFilter && (
            <Button
              variant="outline"
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.delete('lead_id');
                setSearchParams(params);
              }}
            >
              Clear Filter
            </Button>
          )}
        </div>

        {/* Transcripts List */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-sans font-semibold text-gray-900 dark:text-white">
                All Transcripts
              </h3>
              <span className="text-sm text-gray-500">
                {pagination.total} transcript{pagination.total !== 1 ? 's' : ''} total
              </span>
            </div>
          </CardHeader>
          <CardBody>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading transcripts...</p>
              </div>
            ) : transcripts.length === 0 ? (
              <div className="text-center py-12">
                <svg
                  className="mx-auto h-12 w-12 text-gray-400"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
                <h3 className="mt-2 text-sm font-medium text-gray-900">No transcripts</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Transcripts will appear here after meetings are recorded with tl;dv.
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Meeting
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Lead / Company
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Duration
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {transcripts.map((transcript, index) => (
                      <tr
                        key={transcript.meeting_id || index}
                        className="hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleViewTranscript(transcript)}
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {transcript.meeting_title || 'Untitled Meeting'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">
                            {transcript.lead_name || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {transcript.company || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDate(transcript.meeting_date || transcript.created_at)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {formatDuration(transcript.duration_minutes)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getTranscriptStatusBadge(transcript)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewTranscript(transcript);
                            }}
                          >
                            View
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {pagination.pages > 1 && (
              <div className="mt-6 flex items-center justify-between">
                <p className="text-sm text-gray-600">
                  Showing page {currentPage} of {pagination.pages} ({pagination.total} total)
                </p>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === pagination.pages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default TranscriptsPage;
